// context/CategoryTreeContext.js
import React, { createContext, useContext, useState, useCallback } from 'react';

const CategoryTreeContext = createContext();

export const useCategoryTree = () => {
  const context = useContext(CategoryTreeContext);
  if (!context) {
    throw new Error('useCategoryTree must be used within a CategoryTreeProvider');
  }
  return context;
};

export const CategoryTreeProvider = ({ children }) => {
  // ذخیره state باز/بسته شدن دسته‌ها
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  // ذخیره فرزندان لود شده برای هر دسته
  const [loadedChildren, setLoadedChildren] = useState({});

  // تابع برای باز/بسته کردن دسته
  const toggleCategory = useCallback((categoryId) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  }, []);

  // تابع برای اضافه کردن فرزندان لود شده
  const setChildrenForCategory = useCallback((categoryId, children) => {
    setLoadedChildren(prev => ({
      ...prev,
      [categoryId]: children
    }));
  }, []);

  const editChildrenForCategory = useCallback(
  (parentId, categoryId, payload) => {
    setLoadedChildren(prev => {
      // اگر دسته والد وجود ندارد، تغییری ایجاد نکن
      if (!prev[parentId]) return prev;

      // یک کپی عمیق از state قبلی ایجاد کن
      const updatedChildren = { ...prev };

      // لیست زیردسته‌های مربوط به والد را پیدا کن
      const childrenList = updatedChildren[parentId];

      // زیردسته مورد نظر را پیدا کن و تغییرات را اعمال کن
      const updatedList = childrenList.map(child => {
        if (child.id === categoryId) {
          // یک کپی از شیء ایجاد کن و فیلدها را تغییر بده
          return {
            ...child,
            name: payload.name,
            code: payload.code,
            is_active: payload.is_active,
            // اگر می‌خواهی سایر فیلدها را هم آپدیت کنی، می‌تونی اضافه کنی
            // updated_at: new Date().toISOString() // مثلاً
          };
        }
        return child;
      });
            // لیست آپدیت شده را قرار بده
      updatedChildren[parentId] = updatedList;

      return updatedChildren;
    });
  },
  [] // وابستگی‌ها
);



const createChildrenForCategory = useCallback(
  (payload) => {
     // بررسی کن که آیا این parentId از قبل وجود داره یا نه
    const parentId = payload.parent;
    setLoadedChildren(prev => {
      // ایجاد آبجکت جدید برای اضافه شدن
      const newChild = {
        id: payload.id, // بهتره یه id منحصر به فرد بذاری
        name: payload.name,
        description: payload.description,
        parent: payload.parent,
        parent_name: "", // این رو بعداً میتونی پر کنی
        category_image: null,
        slug: "", // معمولاً از name ساخته میشه
        code: payload.code,
        is_active: payload.is_active,
        sort_order: payload.sort_order,
        icon: null,
        created_at: new Date().toISOString(), // بهتره زمان فعلی رو بذاری
        updated_at: new Date().toISOString(),
        attributes: payload.attributes,
        full_path: "", // این رو بعداً میتونی پر کنی
      };

      if (prev[parentId]) {
        // اگر وجود داره، به آرایه موجود اضافه کن
        return {
          ...prev,
          [parentId]: [...prev[parentId], newChild]
        };
      } else {
        // اگر وجود نداره، یه آرایه جدید ایجاد کن
        return {
          ...prev,
          [parentId]: [newChild]
        };
      }
    });
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (!newSet.has(parentId)) {
        newSet.add(parentId);
      }
      return newSet;
    });
  },
  []
);

const removeChildrenForCategory = useCallback(
  (category) => {
    const parentId = category.parent;

    // اگر والد ندارد
    if (!parentId) return;

    setLoadedChildren(prev => {
      // بررسی وجود parentId
      if (!prev[parentId]) return prev;

      // فیلتر کردن
      const updatedChildren = prev[parentId].filter(
        child => child.id !== category.id
      );

      // ایجاد state جدید
      const newState = { ...prev };

      if (updatedChildren.length === 0) {
        delete newState[parentId];
      } else {
        newState[parentId] = updatedChildren;
      }

      return newState;
    });

    // بررسی برای بستن expanded state
    // بعد از آپدیت loadedChildren بررسی کن
    setLoadedChildren(current => {
      if (!current[parentId] || current[parentId].length === 0) {
        setExpandedCategories(prev => {
          const newSet = new Set(prev);
          newSet.delete(parentId);
          return newSet;
        });
      }
      return current;
    });
  },
  []
);

  // بررسی آیا دسته باز است
  const isCategoryExpanded = useCallback((categoryId) => {
    return expandedCategories.has(categoryId);
  }, [expandedCategories]);

  // دریافت فرزندان لود شده برای یک دسته
  const getChildrenForCategory = useCallback((categoryId) => {
    return loadedChildren[categoryId] || [];
  }, [loadedChildren]);

  // پاک کردن تمام stateها (وقتی کاربر از صفحه خارج می‌شود)
  const resetTreeState = useCallback(() => {
    setExpandedCategories(new Set());
    setLoadedChildren({});
  }, []);

  const value = {
    expandedCategories,
    loadedChildren,
    toggleCategory,
    setChildrenForCategory,
    editChildrenForCategory,
    createChildrenForCategory,
    removeChildrenForCategory,
    isCategoryExpanded,
    getChildrenForCategory,
    resetTreeState
  };

  return (
    <CategoryTreeContext.Provider value={value}>
      {children}
    </CategoryTreeContext.Provider>
  );
};